import { useEffect, useState } from "react";
import axiosInstance from "../config/axiosInstance";
import EmptyState from "../components/EmptyState";
import NoData from "../components/NoData";
import { Link, useNavigate } from "react-router-dom";
import { FiBox, FiEdit2, FiTrash2 } from "react-icons/fi";
import { formatDate } from "../utils/format";
import { useAuth } from "../contexts/AuthContext";
import Pagination from "../components/Pagination";

const AdminProductsPage = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [urgencyFilter, setUrgencyFilter] = useState("TODOS");
  const [activeProductId, setActiveProductId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    if (!user || user.role !== "ADMIN") return;
    fetchProducts(currentPage);
  }, [user, currentPage]);

  const fetchProducts = async (page = 1) => {
    setLoadingProducts(true);
    try {
      const response = await axiosInstance.get("/products", {
        params: { itemsPerPage, page },
      });
      setProducts(response.data?.data?.data || []);
      setTotalPages(response.data?.data?.totalPages || 1);
    } catch (error) {
      setProducts([]);
      setTotalPages(1);
    } finally {
      setLoadingProducts(false);
    }
  };

  const resolveMediaSrc = (mediaItem) => {
    if (!mediaItem || typeof mediaItem !== "string") return "";
    if (
      mediaItem.startsWith("data:") ||
      mediaItem.startsWith("blob:") ||
      mediaItem.startsWith("http://") ||
      mediaItem.startsWith("https://")
    ) {
      return mediaItem;
    }
    return `https://relinq.s3.sa-east-1.amazonaws.com/${mediaItem}`;
  };

  const getProductPreviewSrc = (product) => {
    if (Array.isArray(product?.mediaUrls) && product.mediaUrls[0]) {
      return product.mediaUrls[0];
    }
    if (Array.isArray(product?.media) && product.media[0]) {
      return resolveMediaSrc(product.media[0]);
    }
    return "";
  };

  const handleEditProduct = (productId) => {
    navigate(`/produtos/${productId}/editar`);
  };

  const handleDeleteProduct = async (productId) => {
    const confirmed = window.confirm(
      "Deseja realmente excluir este produto? Esta ação não pode ser desfeita."
    );
    if (!confirmed) return;
    setActiveProductId(productId);
    try {
      await axiosInstance.delete(`/products/${productId}`);
      setProducts((currentProducts) =>
        currentProducts.filter((product) => product.id !== productId)
      );
    } catch (error) {
      window.alert("Não foi possível excluir o produto. Tente novamente.");
    } finally {
      setActiveProductId(null);
    }
  };


  // NOVO: categorias pai e subcategorias
  const [categoryFilter, setCategoryFilter] = useState("");
  const [subcategoryFilter, setSubcategoryFilter] = useState("");
  const [categoriesTree, setCategoriesTree] = useState([]);
  const [categoriesLoading, setCategoriesLoading] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      setCategoriesLoading(true);
      try {
        const response = await axiosInstance.get("/categories", {
          params: { itemsPerPage: -1, isActive: true },
        });
        const allCategories = response.data?.data?.data || response.data?.data || [];
        const parents = allCategories.filter((cat) => !cat.parentId);
        const tree = parents.map((parent) => ({
          ...parent,
          subcategories: allCategories.filter((cat) => cat.parentId === parent.id),
        }));
        setCategoriesTree(tree);
      } catch (err) {
        setCategoriesTree([]);
      } finally {
        setCategoriesLoading(false);
      }
    };
    fetchCategories();
  }, []);

  const filteredProducts = products.filter((product) => {
    const title = product?.title || product?.name || "";
    const categoryName =
      product?.Category?.name ||
      product?.category?.name ||
      product?.categoryName ||
      "";
    const categoryId = product?.Category?.id || product?.category?.id || product?.categoryId || "";
    const parentId = product?.Category?.parentId || product?.category?.parentId || product?.parentId || null;
    const condition = product?.priceCondition || "";
    const matchesSearch =
      !searchTerm ||
      [title, categoryName, condition]
        .join(" ")
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
    let matchesCategory = true;
    if (categoryFilter) {
      if (subcategoryFilter) {
        matchesCategory = String(categoryId) === String(subcategoryFilter);
      } else {
        matchesCategory = String(categoryId) === String(categoryFilter) || String(parentId) === String(categoryFilter);
      }
    }
    const matchesUrgency =
      urgencyFilter === "TODOS" ||
      (urgencyFilter === "COM_URGENCIA" && product?.hasUrgency) ||
      (urgencyFilter === "SEM_URGENCIA" && !product?.hasUrgency);
    return matchesSearch && matchesCategory && matchesUrgency;
  });

  if (loading) return <div>Carregando...</div>;
  if (!user || user.role !== "ADMIN") return <div>Acesso restrito</div>;

  // Paginação

  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) setCurrentPage(page);
  };

  return (
    <div className="fade-in page-stack">
      <div className="page-header">
        <div>
          <h2>Todos os Produtos</h2>
          <p>Lista de todos os produtos cadastrados na plataforma.</p>
        </div>
      </div>
      <div className="section-card">
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: "12px",
            alignItems: "end",
            marginBottom: "16px",
          }}
        >
          <div className="field" style={{ margin: 0 }}>
            <label className="label">Buscar produto</label>
            <input
              className="input"
              type="text"
              placeholder="Nome, categoria ou condição"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
            />
          </div>
          <div className="field" style={{ margin: 0, minWidth: 180 }}>
            <label className="label">Categoria</label>
            <select
              className="select"
              value={categoryFilter}
              onChange={e => {
                setCategoryFilter(e.target.value);
                setSubcategoryFilter("");
              }}
              disabled={categoriesLoading}
            >
              <option value="">Todas</option>
              {categoriesTree.map((cat) => (
                <option key={cat.id} value={cat.id}>
                  {cat.name || cat.nome}
                </option>
              ))}
            </select>
          </div>
          {categoryFilter && (
            <div className="field" style={{ margin: 0, minWidth: 180 }}>
              <label className="label">Subcategoria</label>
              <select
                className="select"
                value={subcategoryFilter}
                onChange={e => setSubcategoryFilter(e.target.value)}
                disabled={categoriesLoading}
              >
                <option value="">Todas</option>
                {(categoriesTree.find((cat) => String(cat.id) === String(categoryFilter))?.subcategories || []).map((sub) => (
                  <option key={sub.id} value={sub.id}>
                    {sub.name || sub.nome}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="field" style={{ margin: 0 }}>
            <label className="label">Urgência</label>
            <select
              className="select"
              value={urgencyFilter}
              onChange={(event) => setUrgencyFilter(event.target.value)}
            >
              <option value="TODOS">Todos</option>
              <option value="COM_URGENCIA">Com alerta</option>
              <option value="SEM_URGENCIA">Sem alerta</option>
            </select>
          </div>
        </div>
        {loadingProducts ? (
          <div className="muted">Carregando produtos...</div>
        ) : products.length === 0 ? (
          <NoData title="Nenhum produto cadastrado" description="Nenhum produto foi encontrado na plataforma." />
        ) : filteredProducts.length === 0 ? (
          <NoData title="Nenhum resultado para os filtros" description="Tente ajustar os filtros para encontrar produtos." />
        ) : (
          <>
          <table className="table">
            <thead>
              <tr>
                <th>Produto / Lote</th>
                <th>Categoria</th>
                <th>Condição</th>
                <th>Atualizado</th>
                <th>Anunciante</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filteredProducts.map((product) => {
                // Busca nome da categoria pelo id, se necessário
                let categoryName = product?.Category?.name || product?.category?.name || product?.categoryName;
                const categoryId = product?.Category?.id || product?.category?.id || product?.categoryId;
                if (!categoryName && categoryId && categoriesTree.length > 0) {
                  // Procura em pais
                  const parent = categoriesTree.find(cat => String(cat.id) === String(categoryId));
                  if (parent) categoryName = parent.name || parent.nome;
                  // Procura em subcategorias
                  if (!categoryName) {
                    for (const cat of categoriesTree) {
                      const sub = (cat.subcategories || []).find(sub => String(sub.id) === String(categoryId));
                      if (sub) {
                        categoryName = sub.name || sub.nome;
                        break;
                      }
                    }
                  }
                }
                if (!categoryName) categoryName = "-";
                const price = product?.priceCondition || "-";
                const urgencyBadge = product?.hasUrgency ? (
                  <span
                    style={{
                      backgroundColor: "#ffedd5",
                      color: "#ea580c",
                      padding: "0.25rem 0.5rem",
                      borderRadius: "9999px",
                      fontSize: "0.85rem",
                      fontWeight: "600",
                      whiteSpace: "nowrap",
                    }}
                  >
                    Laranja Alerta
                  </span>
                ) : (
                  <span className="muted">-</span>
                );
                const updatedAt = formatDate(
                  product?.updatedAt || product?.createdAt
                );
                const advertiserName =
                  product?.Company?.nomeFantasia ||
                  product?.Company?.razaoSocial ||
                  "-";
                return (
                  <tr key={product.id}>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.75rem",
                        }}
                      >
                        {getProductPreviewSrc(product) && (
                          <img
                            src={getProductPreviewSrc(product)}
                            alt={product.title}
                            style={{
                              width: "40px",
                              height: "40px",
                              objectFit: "cover",
                              borderRadius: "0.25rem",
                              border: "1px solid var(--color-border)",
                            }}
                          />
                        )}
                        <strong>{product?.title || product?.name || "-"}</strong>
                      </div>
                    </td>
                    <td>{categoryName}</td>
                    <td>{price}</td>
                    <td>{updatedAt}</td>
                    <td>{advertiserName}</td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "0.5rem",
                        }}
                      >
                        <button
                          className="button button--ghost"
                          type="button"
                          onClick={() => handleEditProduct(product.id)}
                          aria-label={`Editar produto ${product?.title || product?.name || ""}`}
                          title="Editar produto"
                          style={{
                            width: "40px",
                            height: "40px",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          <FiEdit2 size={16} />
                        </button>
                        <button
                          className="button button--ghost"
                          type="button"
                          onClick={() => handleDeleteProduct(product.id)}
                          aria-label={`Excluir produto ${product?.title || product?.name || ""}`}
                          title="Excluir produto"
                          disabled={activeProductId === product.id}
                          style={{
                            width: "40px",
                            height: "40px",
                            padding: 0,
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "#b91c1c",
                          }}
                        >
                          <FiTrash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {/* Paginação bonita */}
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={handlePageChange} />
          </>
        )}
      </div>
    </div>
  );
};

export default AdminProductsPage;
